import React, { useCallback, useEffect, useState } from "react";
import {
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
	BreadcrumbItem,
	BreadcrumbSeparator,
} from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	BankChangeRow,
	BankChangeTimelineStep,
	approveBankChange,
	getBankChangeRequests,
	getBankChangeTimeline,
	rejectBankChange,
} from "../../../services/PartnerService";

/**
 * BANK ACCOUNT REVIEW — lớp ② trong bộ 3 lớp Quân duyệt 11/09/2026.
 *
 * Vì sao màn này tồn tại: trước đây điểm tự đổi số tài khoản là có hiệu lực ngay,
 * không ai duyệt, không ai được báo. Nay: KHAI LẦN ĐẦU máy tự duyệt (Quân tick câu 1);
 * ĐỔI TÀI KHOẢN ĐANG DÙNG vào hàng chờ + khoá rút tiền, bấm Approve ở đây thì hệ mới
 * tạo người nhận trên Wise, ghi vào hồ sơ điểm và mở lại quyền rút tiền.
 *
 * Quân test thật 18:06 11/09: "ko xem details được thì duyệt kiểu gì?" → bảng hiện SỐ
 * ĐẦY ĐỦ + nút Details mở toàn bộ ô điểm đã khai (loại pháp nhân, ngân hàng, địa chỉ…)
 * để đối chiếu với hồ sơ. Lỗi Wise (vd "invalid account number") hiện NGUYÊN VĂN.
 *
 * ⚠️ Wise KHÔNG kiểm tên chủ tài khoản qua API (đo 11/09) — máy chỉ chặn được sai
 * định dạng. Người duyệt vẫn phải nhìn: tên chủ tài khoản có khớp hồ sơ pháp lý không.
 *
 * Giao diện TIẾNG ANH như cả Admin (luật Quân 25/08).
 */

const TABS: { key: string; label: string; auto?: boolean }[] = [
	{ key: "PENDING", label: "Pending" },
	{ key: "APPROVED", label: "Approved" },
	{ key: "AUTO", label: "Auto-approved (first-time)", auto: true },
	{ key: "REJECTED", label: "Rejected" },
	{ key: "all", label: "All" },
];

const STATUS_LABEL: Record<string, string> = {
	PENDING: "Pending review",
	APPROVED: "Approved",
	REJECTED: "Rejected",
};

const LEGAL_LABEL: Record<string, string> = { PRIVATE: "Person (private)", BUSINESS: "Business" };

const stationLabel = (r: BankChangeRow) => {
	const name = `${r.station?.name || ""}`.trim() || `${r.station?.displayName || ""}`.trim();
	return name || `Station #${r.partnerId}`;
};

const addressLine = (a: BankChangeRow["chiTiet"]["address"]) => {
	if (!a) return "—";
	return [a.firstLine, a.city, a.state, a.postCode, a.country].filter(Boolean).join(", ") || "—";
};

const BankChangeReview: React.FC = () => {
	const [tab, setTab] = useState<string>("PENDING");
	const [rows, setRows] = useState<BankChangeRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState<string | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);
	const [notice, setNotice] = useState<{ text: string; tone: "ok" | "err" } | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	/* Dòng thời gian theo từng yêu cầu — tải khi mở Details (Quân 23/09: thấy máy/giờ/nơi lúc duyệt). */
	const [timeline, setTimeline] = useState<Record<number, { loading: boolean; steps: BankChangeTimelineStep[]; err: string | null }>>({});

	useEffect(() => {
		if (openId == null || timeline[openId]) return;
		const id = openId;
		setTimeline((t) => ({ ...t, [id]: { loading: true, steps: [], err: null } }));
		getBankChangeTimeline(id)
			.then((steps) => setTimeline((t) => ({ ...t, [id]: { loading: false, steps, err: null } })))
			.catch((e: any) =>
				setTimeline((t) => ({ ...t, [id]: { loading: false, steps: [], err: e?.response?.data?.message || e?.message || "Could not load the timeline." } })),
			);
	}, [openId, timeline]);

	const load = useCallback(async () => {
		setLoading(true);
		setErr(null);
		try {
			const t = TABS.find((x) => x.key === tab) || TABS[0];
			const data = await getBankChangeRequests(t.auto ? "APPROVED" : t.key, Boolean(t.auto));
			setRows(data);
		} catch (e: any) {
			setErr(e?.response?.data?.message || e?.message || "Could not load the list.");
		} finally {
			setLoading(false);
		}
	}, [tab]);

	useEffect(() => {
		load();
	}, [load]);

	const approve = async (row: BankChangeRow) => {
		const ok = window.confirm(
			`Approve the new bank account for ${stationLabel(row)} (#${row.partnerId})?\n\n` +
				`New account: ${row.moi.soDayDu || row.moi.so}\nAccount holder: ${row.moi.chu || "?"}\nBank: ${row.moi.nganHang || "?"} · ${row.currency || "?"}\n` +
				`Address: ${addressLine(row.chiTiet?.address)}\n\n` +
				"After approval, every payout for this station goes to this account. " +
				"Wise does not verify the account holder name — please check it against the station's business documents first.",
		);
		if (!ok) return;
		setBusyId(row.id);
		setNotice(null);
		try {
			const out = await approveBankChange(row.id);
			setNotice({
				text:
					`Approved for ${stationLabel(row)}. Wise recipient: ${out?.wiseRecipientId ?? "?"}` +
					(out?.daMoKhoa ? " · payouts unlocked." : "."),
				tone: "ok",
			});
			await load();
		} catch (e: any) {
			/* 422 = Wise từ chối dữ liệu (nguyên văn trong message). "Network Error" = không tới máy chủ. */
			const msg = e?.response?.data?.message || e?.message || "Approval failed.";
			setNotice({ text: msg, tone: "err" });
		} finally {
			setBusyId(null);
		}
	};

	const reject = async (row: BankChangeRow) => {
		const reason = window.prompt(
			`Reject the request from ${stationLabel(row)} (#${row.partnerId}). Write a reason so the station knows what to fix:`,
			"",
		);
		if (reason == null) return;
		if (reason.trim().length < 5) {
			setNotice({ text: "The reason is too short — the station would not know what to fix.", tone: "err" });
			return;
		}
		setBusyId(row.id);
		setNotice(null);
		try {
			await rejectBankChange(row.id, reason.trim());
			setNotice({ text: `Request #${row.id} rejected. Payouts for the station are unlocked again.`, tone: "ok" });
			await load();
		} catch (e: any) {
			setNotice({ text: e?.response?.data?.message || e?.message || "Rejection failed.", tone: "err" });
		} finally {
			setBusyId(null);
		}
	};

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtContainer>
					<DebtBreadcrumb>
						{/* BreadcrumbSeparator là thẻ <img> — không được nhét chữ (React #137 làm sập SSR, ca thật 11/09). */}
						<BreadcrumbItem as="a" href="/" style={{ textDecoration: "none" }}>
							{"Home"}
						</BreadcrumbItem>
						<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
						<BreadcrumbItem as="a" href="/debt-management" style={{ textDecoration: "none" }}>
							{"Debt Management"}
						</BreadcrumbItem>
						<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
						<BreadcrumbItem active>{"Bank account review"}</BreadcrumbItem>
					</DebtBreadcrumb>
					<DebtTitle>Bank account review</DebtTitle>

					<p style={{ color: "#555", maxWidth: 900, lineHeight: 1.6 }}>
						Stations submit their payout bank account from the station portal. <b>First-time setup</b> is
						verified with Wise and activated automatically. <b>Changing an account already in use</b> is
						validated with Wise, then locks payouts and waits here; approving creates the Wise recipient,
						updates the station profile and unlocks payouts.
						<br />
						<b>Wise checks format and bank codes only</b> — it does not verify the account holder name via
						API. Open <b>Details</b> and compare the name and address with the station's business documents
						before approving.
					</p>

					<div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
						{TABS.map((t) => (
							<button
								key={t.key}
								type="button"
								onClick={() => setTab(t.key)}
								style={{
									padding: "6px 14px",
									borderRadius: 999,
									border: "1px solid #d0d5dd",
									background: tab === t.key ? "#1570ef" : "#fff",
									color: tab === t.key ? "#fff" : "#344054",
									cursor: "pointer",
								}}>
								{t.label}
							</button>
						))}
						<button type="button" onClick={load} disabled={loading} style={{ padding: "6px 14px" }}>
							{loading ? "Loading…" : "Refresh"}
						</button>
					</div>

					{err ? <p style={{ color: "#b42318" }}>⏸ {err}</p> : null}
					{notice ? (
						<p style={{ color: notice.tone === "ok" ? "#027a48" : "#b42318", fontWeight: 600 }}>{notice.text}</p>
					) : null}
					{!loading && !err && rows.length === 0 ? (
						<p style={{ color: "#555" }}>No requests in this view.</p>
					) : null}

					{rows.length ? (
						<div style={{ overflowX: "auto" }}>
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
								<thead>
									<tr style={{ background: "#f9fafb", textAlign: "left" }}>
										<th style={o}>#</th>
										<th style={o}>Station</th>
										<th style={o}>Type</th>
										<th style={o}>Current account</th>
										<th style={o}>New account</th>
										<th style={o}>Status</th>
										<th style={o}>Actions</th>
									</tr>
								</thead>
								<tbody>
									{rows.map((r) => (
										<React.Fragment key={r.id}>
											<tr style={{ borderBottom: openId === r.id ? "none" : "1px solid #eaecf0" }}>
												<td style={o}>{r.id}</td>
												<td style={o}>
													<div style={{ fontWeight: 600 }}>{stationLabel(r)}</div>
													<div style={{ color: "#667085", fontSize: 12 }}>
														Partner #{r.partnerId}
														{r.station?.displayName && r.station?.name ? ` · ${r.station.displayName}` : ""}
													</div>
												</td>
												<td style={o}>
													{r.laLanDau ? (
														<span style={{ color: "#027a48" }}>
															First-time setup{r.tuDong ? " · auto-approved" : ""}
														</span>
													) : (
														<span style={{ color: "#b54708" }}>
															Change of active account
															{r.daKhoaPayout ? " · payouts locked" : ""}
														</span>
													)}
												</td>
												<td style={o}>
													<span style={mono}>{r.cu.soDayDu || r.cu.so}</span>
													<br />
													<span style={{ color: "#667085" }}>
														{r.cu.chu || "—"}
														{r.cu.nganHang ? ` · ${r.cu.nganHang}` : ""}
													</span>
												</td>
												<td style={o}>
													<b style={mono}>{r.moi.soDayDu || r.moi.so}</b>
													<br />
													<span style={{ color: "#667085" }}>
														{r.moi.chu || "—"} · {r.moi.nganHang || "—"} · {r.currency || "—"}
													</span>
													{r.wiseRecipientId ? (
														<>
															<br />
															<span style={{ color: "#667085", fontSize: 12 }}>Wise recipient {r.wiseRecipientId}</span>
														</>
													) : null}
												</td>
												<td style={o}>
													{r.tuDong ? "Approved automatically" : STATUS_LABEL[r.status] || r.status}
													{r.rejectReason ? (
														<>
															<br />
															<span style={{ color: "#667085" }}>{r.rejectReason}</span>
														</>
													) : null}
												</td>
												<td style={o}>
													<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
														<button
															type="button"
															onClick={() => setOpenId(openId === r.id ? null : r.id)}
															style={{ padding: "6px 12px", background: "#fff", color: "#344054", border: "1px solid #d0d5dd", borderRadius: 6, cursor: "pointer" }}>
															{openId === r.id ? "Hide details" : "Details"}
														</button>
														{r.status === "PENDING" ? (
															<>
																<button
																	type="button"
																	onClick={() => approve(r)}
																	disabled={busyId === r.id}
																	style={{ padding: "6px 12px", background: "#1570ef", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer" }}>
																	{busyId === r.id ? "Working…" : "Approve"}
																</button>
																<button
																	type="button"
																	onClick={() => reject(r)}
																	disabled={busyId === r.id}
																	style={{ padding: "6px 12px", background: "#fff", color: "#b42318", border: "1px solid #fda29b", borderRadius: 6, cursor: "pointer" }}>
																	Reject
																</button>
															</>
														) : null}
													</div>
												</td>
											</tr>
											{openId === r.id ? (
												<tr style={{ borderBottom: "1px solid #eaecf0", background: "#f9fafb" }}>
													<td style={o} colSpan={7}>
														<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
															<div>
																<div style={khoi}>Submitted by the station</div>
																<Row k="Recipient type" v={LEGAL_LABEL[r.chiTiet?.legalType || ""] || r.chiTiet?.legalType} />
																<Row k="Account holder name" v={r.chiTiet?.accountHolderName} />
																<Row k="Account number" v={r.chiTiet?.accountNumber} mono />
																<Row k="Bank" v={r.chiTiet?.bankName} />
																<Row k="SWIFT / BIC" v={r.chiTiet?.swiftCode} mono />
																<Row k="Bank code" v={r.chiTiet?.bankCode} mono />
																<Row k="Account type" v={r.chiTiet?.accountType} />
																<Row k="Currency" v={r.currency} />
															</div>
															<div>
																<div style={khoi}>Recipient address (pre-filled from the station profile — check it)</div>
																<Row k="Address" v={r.chiTiet?.address?.firstLine} />
																<Row k="City" v={r.chiTiet?.address?.city} />
																<Row k="State / province" v={r.chiTiet?.address?.state} />
																<Row k="Post code" v={r.chiTiet?.address?.postCode} mono />
																<Row k="Country" v={r.chiTiet?.address?.country} mono />
															</div>
															<div>
																<div style={khoi}>On the station profile now</div>
																<Row k="Account number" v={r.cu.soDayDu} mono />
																<Row k="Account holder" v={r.cu.chu} />
																<Row k="Bank" v={r.cu.nganHang} />
																<Row k="SWIFT / BIC" v={r.cu.swift} mono />
																<Row k="Payment email" v={r.station?.representativeEmail} />
																<Row k="Profile address" v={r.station?.addressText} />
																<Row k="Submitted at" v={r.createdAt ? new Date(r.createdAt).toLocaleString("en-GB") : null} />
															</div>
														</div>
														<Timeline data={timeline[r.id]} />
													</td>
												</tr>
											) : null}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					) : null}
				</DebtContainer>
			</OrderInner>
		</MyBookingWrapper>
	);
};

const Row: React.FC<{ k: string; v?: string | number | null; mono?: boolean }> = ({ k, v, mono: isMono }) => (
	<div style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 13 }}>
		<span style={{ color: "#667085", minWidth: 150 }}>{k}</span>
		<span style={isMono ? mono : undefined}>{v == null || v === "" ? "—" : String(v)}</span>
	</div>
);

/* ── Timeline: login → submitted → payout locked → approved/rejected, each with who/device/where ── */
const STEP_LABEL: Record<BankChangeTimelineStep["key"], string> = {
	login: "Signed in to the station portal",
	submitted: "Submitted the new bank account",
	payout_locked: "Payouts locked pending review",
	approved: "Approved",
	auto_approved: "Auto-approved (first-time setup)",
	rejected: "Rejected",
	status_changed: "Status changed",
};
const SOURCE_LABEL: Record<BankChangeTimelineStep["source"], string> = { station: "Station", admin: "Admin", system: "System" };

const whereText = (s: BankChangeTimelineStep) => {
	const l = s.location;
	if (l?.source === "ipinfo") return [l.city, l.region, l.country].filter(Boolean).join(", ") + (l.org ? ` · ${l.org.replace(/^AS\d+\s*/, "")}` : "");
	if (s.country) return `${s.country} (country only)`;
	if (l?.source === "cloudflare-edge") return "Not captured (record predates real-IP logging, 23 Sep 2026)";
	if (l?.source === "private") return "Internal network";
	return "Unknown";
};

const Timeline: React.FC<{ data?: { loading: boolean; steps: BankChangeTimelineStep[]; err: string | null } }> = ({ data }) => (
	<div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px dashed #d0d5dd" }}>
		<div style={khoi}>Timeline — when, from which device, from where</div>
		{!data || data.loading ? (
			<div style={{ fontSize: 13, color: "#667085" }}>Loading…</div>
		) : data.err ? (
			<div style={{ fontSize: 13, color: "#b42318" }}>{data.err}</div>
		) : data.steps.length === 0 ? (
			<div style={{ fontSize: 13, color: "#667085" }}>No history recorded for this request.</div>
		) : (
			<ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
				{data.steps.map((s, i) => (
					<li key={`${s.key}-${i}`} style={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: 12, padding: "6px 0", fontSize: 13, borderBottom: i < data.steps.length - 1 ? "1px solid #f2f4f7" : undefined }}>
						<span style={{ ...mono, color: "#344054" }}>{s.at ? new Date(s.at).toLocaleString("en-GB") : "—"}</span>
						<span>
							<b>{STEP_LABEL[s.key] || s.key}</b>
							{s.firstTime ? " · first-time setup" : null}
							{s.wiseRecipientId ? ` · Wise recipient ${s.wiseRecipientId}` : null}
							{s.rejectReason ? ` · reason: ${s.rejectReason}` : null}
							<div style={{ color: "#475467", marginTop: 2 }}>
								{SOURCE_LABEL[s.source] || s.source}
								{s.actorEmail ? ` · ${s.actorEmail}` : ""}
								{" · "}
								{s.device?.label || "Unknown device"}
							</div>
							<div style={{ color: "#475467" }}>
								{"Location: "}
								{whereText(s)}
								{s.ip ? <span style={{ ...mono, color: "#98a2b3" }}>{` · IP ${s.ip}`}</span> : null}
							</div>
							{s.note ? <div style={{ color: "#98a2b3", fontStyle: "italic" }}>{s.note}</div> : null}
						</span>
					</li>
				))}
			</ol>
		)}
		<div style={{ fontSize: 12, color: "#98a2b3", marginTop: 6 }}>
			Device comes from the browser's User-Agent (can be spoofed). Location is city-level from the IP, never a street address; VPNs mislead it.
		</div>
	</div>
);

const o: React.CSSProperties = { padding: "10px 12px", verticalAlign: "top" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: 0.3 };
const khoi: React.CSSProperties = { fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "#475467", marginBottom: 6 };

export default BankChangeReview;
